import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getCorrectedURLSearchParams, LocalStorage, useEventSubscribe } from '@novakid/frontend-core';
import { v4 as uuidv4 } from 'uuid';

import { UserContext } from 'contexts';
import { Browser } from 'services';
import { CONFIG } from 'config';
import { useLocation } from 'react-router-dom';

type EventName = `${string}.${string}` | `${string}.${string}.${string}`;

export function useAnalytics() {
  const platformRef = useRef(getCorrectedURLSearchParams(useLocation().search).get('mode'));
  const [sessionId, setSessionId] = useState<string | null>(null);
  const lastUserIdRef = useRef<number | null>(null);
  const user = useContext(UserContext);

  useEffect(() => {
    if (!user?.id) return;

    if (lastUserIdRef.current !== user.id) {
      lastUserIdRef.current = user.id;
      setSessionId(uuidv4());
    }
  }, [user?.id]);

  const sendAnalytics = useCallback(({
    eventName,
    metadata = {},
    commonMetadata = {},
  }: {
    eventName: EventName;
    metadata?: Record<string, unknown>;
    commonMetadata?: { studentId?: number };
  }) => {
    if (!user?.id || !sessionId) return;

    const common = {
      ...commonMetadata,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      platform: platformRef.current === 'mobile' ? 'app' : 'web',
      user_agent: window.navigator.userAgent,
      device_model_name: Browser.osVersion,
      device_brand_name: Browser.osBrand,
      device_type: Browser.platformType,
      location: window.location.href,
      browser: Browser.browserName,
      device_os: Browser.osName,
      user_group: user.group,
      session_id: sessionId,
      user_id: user.id,
    };

    const options = {
      client_timestamp: new Date(),
      event_name: eventName,
      ...metadata,
      ...common,
    };

    const headers = {
      type: 'text/plain',
    };

    const blob = new Blob([JSON.stringify(options)], headers);

    const authToken = LocalStorage.getNVKItem('NVK_AUTH_TOKEN');

    try {
      window.navigator.sendBeacon(`${CONFIG.LOGS_API_URL}?token=${authToken}`, blob);
    } catch (err) {
      console.log('Failed to send vector event', err);
    }

    try {
      if (typeof window.gtag === 'undefined') return;

      window.gtag('event', eventName, {
        event_category: '',
        event_action: eventName,
        event_label: undefined,
        metadata,
      });
    } catch (err) {
      console.log('Failed to send gtag event', err);
    }
  }, [user, sessionId]);

  useEventSubscribe('NVK_SEND_ANALYTICS', sendAnalytics);
}
